/**
 * PublishedPanel — right-rail "Published" tab.
 *
 * FEAT-029 / S-204 + S-205.
 *  - Reads GET /pieces/:id/publish-results (target_type + target_name joined in
 *    by S-202) and renders one card per publish target. The latest result per
 *    target is primary; earlier runs collapse under it.
 *  - For published pieces, an Export section offers on-demand Word/HTML export
 *    via POST /pieces/:id/export (S-203) — independent of configured targets.
 *
 * FEAT-114 / S-457 — PublicationAttention puts the same cards on the piece
 * page for the targets that need something (failed, waiting for a link,
 * publishing), so Retry and the link box are not only inside Piece summary.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi, useApiQuery } from "@/lib/api";
import { matchesSiteUrl, isEligibleAsPrimary, isErrorUrl } from "@shared/publish-url";
import {
  offersLiveUrl,
  urlFormLabel,
  offersRetry,
  failureReason,
  pressKitNotice,
  retryNotice,
  isPublishing,
  needsAttention,
  stepRows,
  type PublishProgress,
  type RetryAnswer,
  type SubstackConfirmOutcome,
} from "@/lib/publish-recovery";
import { apiErrorPayload } from "@/lib/api-error";
import { useRole } from "@/lib/useRole";
import type { PieceDetail } from "@/types/piece";

interface PublishResult {
  id: number;
  publish_target_id: number;
  target_type: string;
  target_name: string;
  external_url: string | null;
  status: string;
  published_at: string | null;
  // FEAT-080 / S-348 — derived by the route, never stored.
  site_url: string | null;
  matches_site_url: boolean | null;
  is_primary: boolean;
  // FEAT-112 / S-444 — a LinkedIn browser job's steps; null for other targets.
  progress?: PublishProgress | null;
}

interface PublishResultsResponse {
  data: PublishResult[];
  meta: { total: number; limit: number; offset: number };
}

interface ExportResponse {
  format: "docx" | "html";
  url: string;
  filename: string;
}

const LINK_TYPES = new Set([
  "substack",
  "wordpress",
  "static_site",
  "linkedin_newsletter",
]);
const FILE_TYPES = new Set(["docx", "html_file"]);

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Most-recent-first by published_at; nulls (pending) sort last. */
function byRecency(a: PublishResult, b: PublishResult): number {
  const ta = a.published_at ? Date.parse(a.published_at) : -Infinity;
  const tb = b.published_at ? Date.parse(b.published_at) : -Infinity;
  return tb - ta;
}

function StatusBadge({ status }: { status: string }) {
  const failed = status === "failed" || status === "error";
  // FEAT-112 / S-444 — not a ✓ until the job says so.
  if (isPublishing({ status })) return <span className="pub-badge is-running">… publishing</span>;
  return (
    <span className={`pub-badge ${failed ? "is-failed" : "is-ok"}`}>
      {failed ? "⚠ " : "✓ "}
      {status}
    </span>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/**
 * FEAT-112 / S-444 — what the browser job has done so far. Each step is
 * ticked once the next one starts; the last stays open while it runs.
 */
function JobSteps({ result }: { result: PublishResult }) {
  const rows = stepRows(result.progress?.steps, isPublishing(result));
  if (rows.length === 0) return null;
  return (
    <ol className="pub-steps">
      {rows.map((r, i) => (
        <li key={`${i}-${r.at}`} className={r.done ? "is-done" : "is-running"}>
          <span className="pub-step-mark">{r.done ? "✓" : "…"}</span>
          <span className="pub-step-label">{r.label}</span>
          <span className="pub-step-time">{fmtTime(r.at)}</span>
        </li>
      ))}
    </ol>
  );
}

function ResultAction({ result }: { result: PublishResult }) {
  const { target_type, external_url, status } = result;
  if (external_url && LINK_TYPES.has(target_type)) {
    return (
      <a className="pub-action" href={external_url} target="_blank" rel="noreferrer">
        ↗ View post
      </a>
    );
  }
  if (external_url && FILE_TYPES.has(target_type)) {
    return (
      <a className="pub-action" href={external_url} target="_blank" rel="noreferrer">
        ↓ Download
      </a>
    );
  }
  return <StatusBadge status={status} />;
}

/**
 * FEAT-080 / S-350 — confirm the real URL for a target the operator published
 * by hand, and say immediately when it does not belong to that target's site.
 *
 * The server stays permissive on purpose (the spec's own instruction: validation
 * gates eligibility, not the write), so this is the only hard stop — and it uses
 * `matchesSiteUrl`, the same function the server uses, so "the form said no" and
 * "the server would not have counted it" can never mean different things.
 */
function ConfirmUrlForm({
  pieceId,
  result,
  label,
}: {
  pieceId: number;
  result: PublishResult;
  label: string;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  // FEAT-107 / BUG-128 — a Substack confirmation also fetches the press kit;
  // say so when it could not, so the missing images are not a mystery.
  const [notice, setNotice] = useState<string | null>(null);

  const typed = url.trim();
  // null — this target has no site configured, so nothing can be checked. Not a
  // mismatch: a docx target has nowhere to live.
  const verdict = typed ? matchesSiteUrl(typed, result.site_url) : null;
  const blocked = verdict === false;

  const save = useMutation({
    mutationFn: async () =>
      api<{ press_kit?: SubstackConfirmOutcome }>(`/pieces/${pieceId}/publish-results/${result.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "published", external_url: typed }),
      }),
    onSuccess: (res) => {
      setUrl("");
      setError(null);
      setNotice(pressKitNotice(res?.press_kit));
      void qc.invalidateQueries({ queryKey: ["piece-publish-results", pieceId] });
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
    },
    onError: () => setError("Could not save that URL — try again."),
  });

  return (
    <div className="pub-confirm">
      <label className="pub-confirm-label" htmlFor={`pub-url-${result.id}`}>
        {label}
      </label>
      <div className="pub-confirm-row">
        <input
          id={`pub-url-${result.id}`}
          className="pub-confirm-input"
          type="url"
          placeholder={result.site_url ? `${result.site_url}/…` : "https://…"}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          className="pub-action"
          disabled={!typed || blocked || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
      {/* The house rule the redesign states: wherever a control is disabled,
          the UI says why. */}
      {blocked && (
        <div className="pub-confirm-warn">
          That is not a {result.site_url} link. Amplification would publish it as
          this piece&rsquo;s article.
        </div>
      )}
      {error && <div className="pub-export-error">{error}</div>}
      {notice && <div className="pub-confirm-warn">{notice}</div>}
    </div>
  );
}

/** FEAT-080 / S-350 — move the canonical link to a different target. */
function SetPrimaryButton({
  pieceId,
  resultId,
}: {
  pieceId: number;
  resultId: number;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: async () =>
      api(`/pieces/${pieceId}`, {
        method: "PATCH",
        body: JSON.stringify({ primary_publish_result_id: resultId }),
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["piece-publish-results", pieceId] });
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
    },
    // Without this a 422 or a dropped connection just reset the label and the
    // operator was left to guess whether it had worked.
    onError: () => setError("Could not set that as primary — try again."),
  });
  return (
    <>
      <button
        type="button"
        className="pub-action"
        disabled={set.isPending}
        onClick={() => set.mutate()}
      >
        {set.isPending ? "Setting…" : "Set as primary"}
      </button>
      {error && <div className="pub-export-error">{error}</div>}
    </>
  );
}

/**
 * FEAT-111 / S-439 — run this one failed target again, in the cloud. A
 * Substack retry publishes the draft the failure left (D6), and answers
 * Substack's login check from 1Password, so it can take up to a minute.
 */
function RetryButton({ pieceId, resultId }: { pieceId: number; resultId: number }) {
  const api = useApi();
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const retry = useMutation({
    mutationFn: async () =>
      api<RetryAnswer>(`/pieces/${pieceId}/publish-results/${resultId}/retry`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: (answer) => {
      setNotice(retryNotice(answer ?? null));
      void qc.invalidateQueries({ queryKey: ["piece-publish-results", pieceId] });
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
    },
    onError: (err) => setNotice(retryNotice(null, apiErrorPayload(err))),
  });
  return (
    <>
      <button
        type="button"
        className="pub-action"
        disabled={retry.isPending}
        onClick={() => {
          setNotice(null);
          retry.mutate();
        }}
      >
        {retry.isPending ? "Retrying…" : "↻ Retry"}
      </button>
      {notice && <div className="pub-confirm-warn">{notice}</div>}
    </>
  );
}

function TargetCard({ pieceId, results }: { pieceId: number; results: PublishResult[] }) {
  const [expanded, setExpanded] = useState(false);
  const [primary, ...earlier] = results;

  // FEAT-108 / S-438 — recording a link and choosing the primary are edits.
  const { canEdit } = useRole();
  const publishing = isPublishing(primary);
  // Not while the job runs: it may still publish, and a hand-entered link would race it.
  const needsUrl = canEdit && !publishing && offersLiveUrl(primary);
  const canRetry = canEdit && offersRetry(primary);
  const reason = failureReason(primary.external_url);

  // Offered only when no result for this target is already the primary —
  // otherwise a re-dispatched target invites the operator to "set" what is
  // effectively already set.
  const canBePrimary =
    canEdit &&
    !results.some((r) => r.is_primary) &&
    isEligibleAsPrimary(primary.status, primary.external_url, primary.site_url);

  return (
    <div className="pub-card">
      <div className="pub-card-head">
        <div className="pub-card-title">{primary.target_name}</div>
        {/* Any result for this target, not just the newest. A target dispatched
            twice claims the primary on the FIRST run, so reading it off
            `primary` (the most recent) made the badge vanish after a
            re-dispatch and offered "Set as primary" on a piece that already had
            one. */}
        {results.some((r) => r.is_primary) && (
          <span className="pub-primary-badge">Primary</span>
        )}
        <span className="pub-card-type">{primary.target_type}</span>
      </div>
      {primary.matches_site_url === false && !isErrorUrl(primary.external_url) && (
        <div className="pub-confirm-warn">
          This URL is not under {primary.site_url}. It will not be used for
          amplification.
        </div>
      )}
      <div className="pub-card-meta">
        {publishing
          ? "Publishing now"
          : primary.published_at
            ? `Published ${fmtDate(primary.published_at)}`
            : primary.status}
      </div>
      <JobSteps result={primary} />
      <div className="pub-card-foot">
        <ResultAction result={primary} />
        {canBePrimary && <SetPrimaryButton pieceId={pieceId} resultId={primary.id} />}
        {canRetry && <RetryButton pieceId={pieceId} resultId={primary.id} />}
      </div>

      {reason && <div className="pub-confirm-warn">Couldn&rsquo;t publish: {reason}</div>}

      {needsUrl && (
        <ConfirmUrlForm
          pieceId={pieceId}
          result={primary}
          label={urlFormLabel(primary)}
        />
      )}

      {earlier.length > 0 && (
        <div className="pub-card-earlier">
          <button className="pub-earlier-toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "▾" : "▸"} {earlier.length} earlier run
            {earlier.length > 1 ? "s" : ""}
          </button>
          {expanded &&
            earlier.map((r) => (
              <div key={r.id} className="pub-earlier-row">
                <span className="pub-earlier-date">
                  {r.published_at ? fmtDate(r.published_at) : r.status}
                </span>
                <ResultAction result={r} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function ExportSection({ pieceId }: { pieceId: number }) {
  const api = useApi();
  const [busy, setBusy] = useState<null | "docx" | "html">(null);
  const [error, setError] = useState<string | null>(null);

  async function run(format: "docx" | "html") {
    setBusy(format);
    setError(null);
    try {
      const res = await api<ExportResponse>(`/pieces/${pieceId}/export`, {
        method: "POST",
        body: JSON.stringify({ format }),
      });
      // Programmatic anchor — more reliable than window.open after an await
      // (which popup blockers can reject outside the direct click gesture).
      const a = document.createElement("a");
      a.href = res.url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Export failed — try again");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="pub-export">
      <div className="pub-export-title">Export this piece</div>
      <div className="pub-export-actions">
        <button
          className="pub-export-btn"
          onClick={() => void run("docx")}
          disabled={busy !== null}
        >
          {busy === "docx" ? "Generating…" : "⬇ Word (.docx)"}
        </button>
        <button
          className="pub-export-btn"
          onClick={() => void run("html")}
          disabled={busy !== null}
        >
          {busy === "html" ? "Generating…" : "⬇ HTML"}
        </button>
      </div>
      {error && <div className="pub-export-error">{error}</div>}
    </div>
  );
}

/** One list per publish target, newest result first. */
function usePublishResultsByTarget(pieceId: number) {
  const { data, isLoading } = useApiQuery<PublishResultsResponse>(
    ["piece-publish-results", pieceId],
    `/pieces/${pieceId}/publish-results`,
    // FEAT-112 / S-444 — follow a running LinkedIn job; stop once it settles.
    { refetchInterval: (q) => (q.state.data?.data.some(isPublishing) ? 3000 : false) }
  );

  const grouped = useMemo(() => {
    const rows = data?.data ?? [];
    const byTarget = new Map<number, PublishResult[]>();
    for (const r of rows) {
      const list = byTarget.get(r.publish_target_id) ?? [];
      list.push(r);
      byTarget.set(r.publish_target_id, list);
    }
    return [...byTarget.values()].map((list) => list.slice().sort(byRecency));
  }, [data]);

  return { grouped, isLoading };
}

/**
 * FEAT-114 / S-457 — the targets that need something, on the piece page.
 * Renders nothing when every target is published or scheduled.
 */
export function PublicationAttention({ pieceId }: { pieceId: number }) {
  const { grouped } = usePublishResultsByTarget(pieceId);
  const attention = grouped.filter((results) => needsAttention(results[0]));
  if (attention.length === 0) return null;
  return (
    <section className="pub-attention" aria-label="Publication">
      <div className="pub-attention-title">Publication</div>
      <div className="pub-cards">
        {attention.map((results) => (
          <TargetCard key={results[0].publish_target_id} pieceId={pieceId} results={results} />
        ))}
      </div>
    </section>
  );
}

interface Props {
  piece: PieceDetail;
}

export function PublishedPanel({ piece }: Props) {
  const pieceId = piece.id;
  const { grouped, isLoading } = usePublishResultsByTarget(pieceId);

  const isPublished = piece.status === "published";

  return (
    <div className="panel-body">
      <div className="panel-header">
        <h3>Published</h3>
        <span className="panel-sub">Live links, files & export</span>
      </div>

      {isPublished && <ExportSection pieceId={pieceId} />}

      {isLoading ? (
        <div className="panel-empty">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="panel-empty">
          No publish results yet.
          <br />
          Run the pipeline to generate and publish this piece.
        </div>
      ) : (
        <div className="pub-cards">
          {grouped.map((results) => (
            <TargetCard key={results[0].publish_target_id} pieceId={pieceId} results={results} />
          ))}
        </div>
      )}
    </div>
  );
}
